// A stored package entry in the shape the Rust core takes.
//
// Without this every call across the wasm boundary throws (#214).
// `core-ffi`'s `PackageEntry` is six scalars; a document in `packageEntries`
// is not, in two ways:
//
//   * **`minutes` is written on every kind (#181)** — `sellPackage` writes
//     the template's duration and `useAttendance` writes the occurrence's. An
//     absent one still crosses as `null`, and what an absent count means is the
//     core's to say (#253, below).
//
//   * **`voidedAt` is a `serverTimestamp()`.** It reads back as a Firestore
//     `Timestamp` once the server has acknowledged the write, and as **null**
//     from this device's own cache until then. That gap is issue #70 and it is
//     the whole reason a tombstone is two fields: `voided` carries the fact,
//     `voidedAt` is the audit trail. The core only ever asks whether this is
//     present, so a resolved timestamp crosses as its ISO string and an
//     unresolved one crosses as null.
//
// ── What a skipped conversion looks like ───────────────────────────────────
//
// `test/core/packageShape.wasm.test.js` asserts each of these against the
// built artifact:
//
//   missing key         `missing field \`voided\`` — **names the field.**
//   wrong type          `invalid type: string "10", expected i64 — reading
//                       \`minutes\` of PackageEntry`. It names the field too
//                       (#275).
//   Timestamp           `invalid type: JsValue(Object({…})), expected a string
//                       — reading \`voidedAt\` of PackageEntry`
//   absent `voidedAt`   accepted. An `Option` field simply missing is `None`,
//                       which is what the app means by null anyway.
//   absent `minutes`    accepted (#253), for the same reason — and what an
//                       absent count *means* is the core's, not this file's.
//
// Every field is converted, not only the interesting ones.
//
// The unnameable "memory access out of bounds" of #215 is the *bare string
// parameter* ABI, not this one: a field arriving inside a tsify record always
// says at least what type it wanted.
//
// Separate from `usePackages.jsx` so it can be exercised against the real wasm
// without a Firestore handle — the `ui` project cannot reach Firebase. Same
// reason `opCodec.js` sits beside its provider rather than inside it.
//
// ── Which kind of conversion this is, and why it is not the shared one ─────
//
// `src/data/docShape.js` is the shared helper (#219), and this file is not it,
// because the two cross different boundaries:
//
//   * `docShape.js` serves `reconcileDocument`, which takes a flat `DocField[]`
//     of `String`s and never reads a value. That is **carriage**, it is the
//     same for every collection, and it is written once.
//   * this file serves `packageSummary` and friends, which take `PackageEntry`
//     — six fields the core has **named and typed**. That is a **projection**,
//     and a projection is per-type by construction: no shared helper can know
//     that `core-ffi` wants `minutes` as an `i64`.
//
// The rule for the next module is the one in `docShape.js`'s header: if the
// core reads the value, the conversion is a projection and it is yours; if the
// core only carries it, use `docShape.js`. What this file does share is the one
// decision both kinds make — *what instant does this value denote, without
// importing Firestore* — and it calls `isoInstantOf` for that.
//
// ── What an absent `minutes` means is not this file's ─────────────────────
//
// `core-ffi`'s `PackageEntry.minutes` is an `Option<i64>` (#253), so the
// absent case crosses as `null` and `coda_domain::packages::signed_minutes`
// says what it means. This file's half is a stored JavaScript number becoming
// an integer. The `|| 0` inside it is `Number('')`/`Number('abc')`, a garbage
// *present* value, which `js_number_or_zero` reads the same way on the other
// side of the boundary.
//
// The field is `minutes` (#181): the name is what tells a reader of a stored
// document which unit it is in.
import { isoInstantOf } from './docShape.js';

/**
 * One `packageEntries` document as `core-ffi`'s `PackageEntry`.
 *
 * Total: every field is given a value, because the boundary has no defaults.
 */
export function toCoreEntry(e) {
  const at = e?.voidedAt;
  return {
    id: String(e?.id ?? ''),
    kind: String(e?.kind ?? ''),
    // Absent stays absent. Present is coerced to the integer the boundary
    // names, and nothing here decides what absent means — see the header.
    minutes: e?.minutes == null ? null : Math.round(Number(e.minutes) || 0),
    occurredOn: String(e?.occurredOn ?? ''),
    voided: Boolean(e?.voided),
    // A string passes through so the core can apply its empty-string rule to
    // it, and the `String(at)` fallback stays: something neither null, a string
    // nor an instant is still *present*, and reducing it to null would turn a
    // tombstone back into a live entry — #70's direction.
    voidedAt: at == null
      ? null
      : (typeof at === 'string' ? at : (isoInstantOf(at) ?? String(at))),
  };
}

/**
 * The expiry dates in a student's entries, as `core-ffi`'s `PackageExpiry[]`
 * (#368).
 *
 * **A second projection off the same documents, rather than a seventh field on
 * `toCoreEntry`.** `core-ffi`'s `PackageEntry` does not carry `expiresOn` —
 * Kotlin builds that record positionally in `Packages.Entry.core()` — and only
 * the expiry folds read the date. See the crate header on `packages::Expiry`.
 *
 * A purchase with no date is left out; the core drops an empty date anyway. A
 * **tombstoned** purchase is left in and dropped on the other side, because
 * "is this entry live" is the core's (`packages::is_active`), as the
 * absent-count default is (#253).
 */
export function toCoreExpiries(entries) {
  const out = [];
  for (const e of entries || []) {
    if (e?.kind !== 'purchase') continue;
    const on = e?.expiresOn;
    if (typeof on !== 'string' || on === '') continue;
    out.push({ entryId: String(e?.id ?? ''), on });
  }
  return out;
}
